import { ReadonlyVec3, vec3 } from 'gl-matrix';
import {
    dcmjsWriteRoiSequenceProperty,
    dcmjsWriteContourSequenceProperty,
    dcmjsReadRoiObservationsProperty,
    dcmjsWriteRoiObservationsProperty,
    dcmjsReadRoiPhysicalPropertyValue,
    dcmjsWriteRoiPhysicalPropertyValue,
    RoiPhysicalPropertyType,
    dcmjsReadRoiSequenceProperty,
    dcmjsGetString,
} from '../services/dicomProcessor';
import { DcmJsDatasetWrapper } from './dcmJsWrapper';
import { DicomColor } from './dicomColor';
import { DICOMApprovalStatus, DicomVRMaxLength } from './dicomConstants';
import {
    ContourEditBlocker,
    resolveContourEditBlocker,
} from './contourEditBlocker';
import {
    ContourGeometricType,
    DicomContour,
    ROI_POINT_TYPES,
    RoiGenerationAlgorithmType,
    RoiIdentificationCodeSequence,
    RoiInterpretedType,
} from './dicomstructureset';

/**
 * Represents a Region of Interest (ROI) (aka "structure" in UI) in a DICOM RT Structure Set.
 * Encapsulates properties and methods for managing ROI attributes such as name, color, contours,
 * approval status, interpreted type, identification code, physical properties, description, and generation algorithm.
 * Provides accessors and mutators for DICOM-backed properties, enforcing modification constraints
 * based on approval status.
 *
 * @remarks
 * This class interacts with DICOM datasets via a wrapper and utility functions to read and write ROI-related attributes.
 * Modification of certain properties is restricted if the ROI or its parent structure set is approved.
 *
 * @example
 * ```typescript
 * const roi = new Roi(datasetWrapper, 1, "Tumor", { R: 255, G: 0, B: 0 });
 * roi.name = "Updated Tumor";
 * roi.color = { R: 0, G: 255, B: 0 };
 * ```
 */
export class DicomRoi {
    public readonly roiNumber: number;
    private _name: string;
    private _visible: boolean;
    private _contours: DicomContour[] = [];
    private _color: DicomColor;
    private _contourThickness: number;
    private _locked = false;
    private _approvalStatus: DICOMApprovalStatus =
        DICOMApprovalStatus.UNAPPROVED;

    /**
     * Creates a new ROI (Region of Interest) instance.
     *
     * @param structureSetDatasetWrapper - The DcmJsDatasetWrapper containing the structure set dataset.
     * @param roiNumber - The unique number identifying the ROI.
     * @param name - The name of the ROI.
     * @param color - The display color of the ROI, defaults to { R: 127, G: 127, B: 127 } if not specified.
     */
    constructor(
        private readonly structureSetDatasetWrapper: DcmJsDatasetWrapper,
        roiNumber: number,
        name: string,
        color: DicomColor = { R: 127, G: 127, B: 127 }
    ) {
        this.roiNumber = roiNumber;
        this._name = name;
        this._color = color;
        this._visible = true;
        this._contourThickness = 1;
    }

    public get contours(): DicomContour[] {
        return this._contours;
    }

    public set contours(contours: DicomContour[]) {
        this._contours = contours;
    }

    public get approvalStatus(): DICOMApprovalStatus {
        return this._approvalStatus;
    }

    public set approvalStatus(newStatus: DICOMApprovalStatus) {
        this._approvalStatus = newStatus;
    }

    public get isApproved(): boolean {
        return this.approvalStatus === DICOMApprovalStatus.APPROVED;
    }

    /** Whether this ROI can be modified (not approved, and parent set not approved) */
    public get canModify(): boolean {
        return !this.isApproved && !this._isStructureSetApproved();
    }

    /**
     * Per-ROI edit lock — softer than approval: freezes geometry (see
     * {@link canModifyContours}) but leaves metadata editable. DICOM defines no
     * lock attribute, so it is never written to the dataset and never exported;
     * it is carried by the structure set's database row instead, and reaches
     * that row only when the set is saved — the UI must disclose this.
     */
    public get locked(): boolean {
        return this._locked;
    }

    public set locked(newLocked: boolean) {
        this._locked = newLocked;
    }

    /**
     * Single seam every geometry mutator gates on: approval and the edit
     * lock both block a shape change.
     */
    public get canModifyContours(): boolean {
        return this.canModify && !this._locked;
    }

    /**
     * Which of the two guards refuses a shape change, or undefined when
     * neither does. The one answer every refusal is worded from, so no caller
     * has to re-derive whether approval reaches this ROI through its own
     * status or through its parent set.
     */
    public get contourEditBlocker(): ContourEditBlocker | undefined {
        return resolveContourEditBlocker(!this.canModify, this._locked);
    }

    public get contourThickness(): number {
        return this._contourThickness;
    }

    public set contourThickness(newThickness: number) {
        this._contourThickness = newThickness;
    }

    public get visible(): boolean {
        return this._visible;
    }

    public set visible(newVisible: boolean) {
        this._visible = newVisible;
    }

    /**
     * ROI Name Attribute Tag (3006,0026)
     */
    public get name(): string {
        return this._name;
    }

    public set name(newName: string) {
        this._ensureCanModify();

        if (newName.length > DicomVRMaxLength.LONG) {
            newName = newName.substring(0, DicomVRMaxLength.LONG);
        }
        this._name = newName;
        dcmjsWriteRoiSequenceProperty(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'ROIName',
            newName
        );
    }

    /**
     * ROI Display Color Attribute Tag (3006,002A)
     */
    public get color(): DicomColor {
        return this._color;
    }

    public set color(newColor: DicomColor) {
        this._color = newColor;
        dcmjsWriteContourSequenceProperty(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'ROIDisplayColor',
            [newColor.R, newColor.G, newColor.B]
        );
    }

    /**
     * RT ROI Interpreted Type Attribute Tag (3006,00A4)
     */
    public get interpretedType(): RoiInterpretedType {
        const val = dcmjsReadRoiObservationsProperty<RoiInterpretedType>(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'RTROIInterpretedType'
        );
        return val ? val : RoiInterpretedType.UNKNOWN;
    }

    public set interpretedType(newType: RoiInterpretedType) {
        dcmjsWriteRoiObservationsProperty(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'RTROIInterpretedType',
            newType
        );
    }

    /**
     * RT ROI Identification Code Sequence Attribute Tag (3006,0086)
     */
    public get identificationCode(): RoiIdentificationCodeSequence | undefined {
        const val = dcmjsReadRoiObservationsProperty<
            {
                CodeValue: string;
                CodingSchemeDesignator: string;
                CodingSchemeVersion: string;
                CodeMeaning: string;
            }[]
        >(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'RTROIIdentificationCodeSequence'
        );
        return val && val.length === 1
            ? {
                  codeValue: val[0].CodeValue,
                  codingSchemeDesignator: val[0].CodingSchemeDesignator,
                  codingSchemeVersion: val[0].CodingSchemeVersion,
                  codeMeaning: val[0].CodeMeaning,
              }
            : undefined;
    }

    public set identificationCode(code: RoiIdentificationCodeSequence) {
        dcmjsWriteRoiObservationsProperty(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'RTROIIdentificationCodeSequence',
            [
                {
                    CodeValue: code.codeValue,
                    CodingSchemeDesignator: code.codingSchemeDesignator,
                    CodingSchemeVersion: code.codingSchemeVersion,
                    CodeMeaning: code.codeMeaning,
                },
            ]
        );
    }

    /**
     * Relative electron density override
     * (`ROIPhysicalProperty` = `REL_ELEC_DENSITY`) read from
     * `ROIPhysicalPropertiesSequence` (3006,00B0) /
     * `ROIPhysicalPropertyValue` (3006,00B4). Unitless.
     *
     * Writing `undefined` removes the electron density entry while preserving
     * any mass density entry on the same ROI.
     */
    public get electronDensityOverride(): number | undefined {
        return dcmjsReadRoiPhysicalPropertyValue(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            RoiPhysicalPropertyType.REL_ELEC_DENSITY
        );
    }

    public set electronDensityOverride(newValue: number | undefined) {
        dcmjsWriteRoiPhysicalPropertyValue(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            RoiPhysicalPropertyType.REL_ELEC_DENSITY,
            newValue
        );
    }

    /**
     * Relative mass density override
     * (`ROIPhysicalProperty` = `REL_MASS_DENSITY`) read from
     * `ROIPhysicalPropertiesSequence` (3006,00B0) /
     * `ROIPhysicalPropertyValue` (3006,00B4). Unitless.
     *
     * Writing `undefined` removes the mass density entry while preserving any
     * electron density entry on the same ROI.
     */
    public get massDensityOverride(): number | undefined {
        return dcmjsReadRoiPhysicalPropertyValue(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            RoiPhysicalPropertyType.REL_MASS_DENSITY
        );
    }

    public set massDensityOverride(newValue: number | undefined) {
        dcmjsWriteRoiPhysicalPropertyValue(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            RoiPhysicalPropertyType.REL_MASS_DENSITY,
            newValue
        );
    }

    /**
     * ROI Description Attribute Tag (3006,0028)
     */
    public get roiDescription(): string {
        const val = dcmjsReadRoiSequenceProperty<RoiGenerationAlgorithmType>(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'ROIDescription'
        );
        return val ? val : '';
    }

    public set roiDescription(newDescription: string) {
        let processedDescription = newDescription;
        if (processedDescription.length > DicomVRMaxLength.TEXT) {
            processedDescription = processedDescription.substring(
                0,
                DicomVRMaxLength.TEXT
            );
        }

        dcmjsWriteRoiSequenceProperty(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'ROIDescription',
            processedDescription
        );
    }

    /**
     * ROI Generation Algorithm Attribute Tag (3006,0036)
     */
    public get roiGenerationAlgorithm(): RoiGenerationAlgorithmType {
        const val = dcmjsReadRoiSequenceProperty<RoiGenerationAlgorithmType>(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'ROIGenerationAlgorithm'
        );
        return val ? val : RoiGenerationAlgorithmType.UNKNOWN;
    }

    public set roiGenerationAlgorithm(
        newAlgorithm: RoiGenerationAlgorithmType
    ) {
        dcmjsWriteRoiSequenceProperty(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'ROIGenerationAlgorithm',
            newAlgorithm
        );
    }

    /**
     * ROI Generation Description Attribute Tag (3006,0038)
     */
    public get roiGenerationDescription(): string {
        const val = dcmjsReadRoiSequenceProperty<string>(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'ROIGenerationDescription'
        );
        return val ? val : '';
    }

    public set roiGenerationDescription(newDescription: string) {
        let processedDescription = newDescription;
        if (processedDescription.length > DicomVRMaxLength.LONG) {
            processedDescription = processedDescription.substring(
                0,
                DicomVRMaxLength.LONG
            );
        }

        dcmjsWriteRoiSequenceProperty(
            this.structureSetDatasetWrapper,
            this.roiNumber,
            'ROIGenerationDescription',
            processedDescription
        );
    }

    /** Whether this ROI represents a single point (MARKER, ISOCENTER, REGISTRATION). */
    public get isPointRoi(): boolean {
        return ROI_POINT_TYPES.includes(this.interpretedType);
    }

    /**
     * Updates the coordinates of a point-type ROI. Replaces all
     * existing contours with a single POINT contour, preserving
     * any referenced SOP UIDs from a previous POINT contour.
     *
     * @param coordinatesMm - The new point coordinates in mm
     * @throws If this ROI is not a point type or cannot be modified
     */
    public setPointCoordinates(coordinatesMm: ReadonlyVec3): void {
        this._ensureCanModify();
        if (!this.isPointRoi) {
            throw new Error(
                `Cannot set point coordinates on non-point ROI ` +
                    `'${this._name}'`
            );
        }

        const existingPoint = this._contours.find(
            (c) => c.type === ContourGeometricType.POINT
        );
        this._contours = [
            new DicomContour(
                ContourGeometricType.POINT,
                [
                    vec3.fromValues(
                        coordinatesMm[0],
                        coordinatesMm[1],
                        coordinatesMm[2]
                    ),
                ],
                existingPoint?.referencedSOPClassUID,
                existingPoint?.referencedSOPInstanceUID
            ),
        ];
    }

    /**
     * Extracts the single-point coordinate for point-type ROIs.
     * Prefers an explicit POINT contour; falls back to centroid
     * of the first contour for point-interpreted-type ROIs.
     *
     * @returns The point coordinates in mm, or null if not a point ROI
     *          or if no contour data is available.
     */
    public get pointCoordinatesMm(): ReadonlyVec3 | null {
        // Prefer explicit POINT contour
        const pointContour = this._contours.find(
            (c) => c.type === ContourGeometricType.POINT && c.data.length >= 1
        );
        if (pointContour) {
            return pointContour.data[0];
        }

        // For point-like interpreted types (MARKER, ISOCENTER,
        // REGISTRATION), fall back to centroid of first contour
        if (
            this.isPointRoi &&
            this._contours.length > 0 &&
            this._contours[0].data.length > 0
        ) {
            const data = this._contours[0].data;
            let x = 0;
            let y = 0;
            let z = 0;
            for (const pt of data) {
                x += pt[0];
                y += pt[1];
                z += pt[2];
            }
            const n = data.length;
            return vec3.fromValues(x / n, y / n, z / n) as ReadonlyVec3;
        }

        return null;
    }

    private _ensureCanModify() {
        if (this.isApproved) {
            throw new Error(`Cannot modify approved structure '${this._name}'`);
        }
        if (this._isStructureSetApproved()) {
            throw new Error(
                `Cannot modify structure '${this.name}' in approved structure set`
            );
        }
    }

    /** The approval status of the structure set this ROI belongs to. */
    public get structureSetApprovalStatus(): string {
        return dcmjsGetString(
            this.structureSetDatasetWrapper,
            'ApprovalStatus'
        );
    }

    // helper to read the structure set approval status from the DICOM dataset
    private _isStructureSetApproved(): boolean {
        return this.structureSetApprovalStatus === DICOMApprovalStatus.APPROVED;
    }
}
